<template>
  <div>
    <div class="main-container">
      <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

      <!-- Back nav -->
      <div class="profile-section mb-2">
        <div class="pl-2">
          <a
            class="text-black"
            href="javascript:void(0)"
            @click="$router.push({ path: '/kids-device', query: { profileId } })"
          >
            <i class="fas fa-long-arrow-alt-left"></i> <b>Back</b>
          </a>
        </div>
      </div>

      <div class="profile-section pt-2">
        <div class="container">

          <!-- Top action bar -->
          <div class="top-action-bar mb-3">
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <button
                v-if="hasChatAccess"
                class="btn btn-outline-secondary btn-sm action-btn"
                :disabled="isDisabled"
                @click="$router.push({ path: '/chats', query: { profileId, serialNumber } })"
              >
                <i class="fas fa-comment-dots"></i> View SMS
              </button>
              <button
                v-if="hasChatAccess"
                class="btn btn-outline-secondary btn-sm action-btn"
                :disabled="isDisabled"
                @click="$router.push({ path: '/call-history', query: { profileId, serialNumber } })"
              >
                <i class="fas fa-phone"></i> Call History
              </button>

              <button
                class="btn btn-outline-secondary btn-sm action-btn"
                @click="openSettingsModal"
              >
                <i class="fas fa-cog"></i> Settings
              </button>

              <!-- Requests button — badge only visible when there are pending requests -->
              <button
                class="btn btn-outline-secondary btn-sm action-btn request-btn"
                @click="openRequestsModal"
              >
                <i class="fas fa-user-clock"></i> Requests
                <span v-if="totalPendingRequests > 0" class="request-badge">
                  {{ totalPendingRequests > 99 ? '99+' : totalPendingRequests }}
                </span>
              </button>

              <button
                class="btn btn-primary btn-sm action-btn ml-auto"
                @click="openAddModal"
              >
                <i class="fas fa-user-plus"></i> Add Contact
              </button>
              <button
                v-if="isContactPickerSupported"
                :disabled="importing"
                class="btn btn-outline-primary btn-sm ml-2"
                @click.stop="importFromPhone"
              >
                <i v-if="!importing" class="fas fa-address-book"></i>
                <i v-else class="fas fa-spinner fa-spin"></i>
                Import from Phone
              </button>
            </div>
          </div>

          <!-- Wingio Dialer disabled — page-level warning + lockout -->
          <div v-if="wingioDialerLoaded && !settings.enableWingioDialer" class="page-dialer-banner mb-3">
            <div class="pdb-icon"><i class="fas fa-phone-slash"></i></div>
            <div class="pdb-body">
              <p class="pdb-title">Wingio Dialer is disabled</p>
              <p class="pdb-desc">
                Contact restrictions are inactive because the Wingio Dialer app is not enabled on
                this device. Contacts are still saved but call and SMS rules will not be enforced
                until you enable Wingio Dialer in <a href="javascript:void(0)" @click="openSettingsModal" class="pdb-link">Settings</a>.
              </p>
            </div>
          </div>

          <!-- Contacts list card -->
          <div :class="{ 'page-contacts-lockout': wingioDialerLoaded && !settings.enableWingioDialer }">
            <div class="contacts-card">
              <div class="contacts-header">
                <div class="header-left">
                  <div class="contacts-icon">
                    <i class="fas fa-address-book"></i>
                  </div>
                  <div>
                    <h3 class="contacts-title">Saved Contacts</h3>
                    <p class="contacts-count">
                      {{ contacts.length }} contact{{ contacts.length !== 1 ? 's' : '' }}
                    </p>
                  </div>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table contacts-table mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th class="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="contacts.length === 0">
                      <td colspan="3" class="empty-state">
                        <div class="empty-icon"><i class="fas fa-address-book"></i></div>
                        <p class="empty-text">No contacts added yet</p>
                        <p class="empty-subtext">Start by adding your first contact above</p>
                      </td>
                    </tr>
                    <tr
                      v-for="contact in contacts"
                      :key="contact._id"
                      class="contact-row"
                      :class="{ 'emergency-row': contact.isEmergency }"
                    >
                      <td class="contact-name">
                        <div class="name-wrapper">
                          <div class="avatar">
                            <img
                              v-if="contact.imageUrl"
                              :src="contact.imageUrl"
                              class="avatar-img"
                              alt=""
                            />
                            <span v-else>{{ getInitials(contact.name) }}</span>
                          </div>
                          <div class="name-info">
                            <div class="name-row">
                              <span class="name">{{ contact.name }}</span>
                              <span v-if="contact.isEmergency" class="emergency-pill" title="Emergency contact">
                                <i class="fas fa-shield-alt"></i> 
                              </span>
                            </div>
                            <span
                              v-if="contact.email && contact.email !== 'undefined'"
                              class="email"
                            >{{ contact.email }}</span>
                          </div>
                        </div>
                      </td>
                      <td class="contact-phone">
                        <a :href="'tel:' + contact.phone" class="phone-link">
                          {{ contact.phone }}
                        </a>
                        <div v-if="contact.notes" class="phone-notes">{{ contact.notes }}</div>
                      </td>
                      <td class="contact-actions text-center">
                        <button class="btn-edit" @click="openEditModal(contact)" title="Edit">
                          <i class="fas fa-pencil-alt"></i>
                        </button>
                        <button
                          class="btn-delete ml-1"
                          @click="openDeleteModal(contact._id)"
                          title="Delete"
                        >
                          <i class="fas fa-trash-alt"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <!-- /contacts list -->

        </div>
      </div>

      <!-- ADD CONTACT MODAL -->
      <div class="modal fade" id="addContactModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content cmodal-content text-left">
            <div class="modal-header cmodal-header">
              <div class="d-flex align-items-center">
                <div class="cmodal-icon blue"><i class="fas fa-user-plus"></i></div>
                <h5 class="modal-title ml-2">Add New Contact</h5>
              </div>
              <button type="button" class="close" data-dismiss="modal"><span>&times;</span></button>
            </div>
            <div class="modal-body cmodal-body">
              <div class="form-group modern-input">
                <label class="input-label"><i class="fas fa-user"></i> Name</label>
                <input type="text" v-model="addForm.name" class="form-control" placeholder="Enter full name" autofocus />
              </div>
              <div class="form-group modern-input">
                <label class="input-label"><i class="fas fa-phone"></i> Phone Number</label>
                <input type="text" v-model="addForm.phone" class="form-control" placeholder="Enter phone number" />
              </div>
              <div class="form-group modern-input">
                <label class="input-label"><i class="fas fa-image"></i> Profile Photo</label>
                <input type="file" @change="handleAddFileUpload" class="form-control-file mt-1" />
                <img v-if="addForm.previewUrl" :src="addForm.previewUrl" class="img-fluid rounded border mt-2" style="max-width:80px" />
              </div>
              <div class="form-group modern-input">
                <label class="input-label"><i class="fas fa-sticky-note"></i> Notes</label>
                <textarea class="form-control" v-model="addForm.notes" placeholder="e.g. Mom, Dad, call after school" rows="2"></textarea>
              </div>
              <div class="emergency-checkbox-row">
                <input type="checkbox" id="addIsEmergency" v-model="addForm.isEmergency" />
                <label for="addIsEmergency">
                  <span class="ec-title"><i class="fas fa-shield-alt"></i> Mark as emergency contact</span>
                  <span class="ec-desc">Emergency contacts can always call/message this device even when restrictions are active</span>
                </label>
              </div>
            </div>
            <div class="modal-footer cmodal-footer">
              <button type="button" class="btn btn-outline-secondary" data-dismiss="modal"><i class="fas fa-times"></i> Cancel</button>
              <button type="button" class="btn btn-primary" @click="addContact" :disabled="isAdding">
                <i v-if="!isAdding" class="fas fa-plus-circle"></i>
                <i v-else class="fas fa-spinner fa-spin"></i>
                {{ isAdding ? 'Adding...' : 'Add Contact' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- EDIT CONTACT MODAL -->
      <div class="modal fade" id="editContactModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content cmodal-content text-left">
            <div class="modal-header cmodal-header">
              <div class="d-flex align-items-center">
                <div class="cmodal-icon purple"><i class="fas fa-user-edit"></i></div>
                <h5 class="modal-title ml-2">Edit Contact</h5>
              </div>
              <button type="button" class="close" data-dismiss="modal"><span>&times;</span></button>
            </div>
            <div class="modal-body cmodal-body">
              <div class="form-group modern-input">
                <label class="input-label"><i class="fas fa-user"></i> Name</label>
                <input type="text" v-model="editForm.name" class="form-control" placeholder="Enter full name" />
              </div>
              <div class="form-group modern-input">
                <label class="input-label"><i class="fas fa-phone"></i> Phone Number</label>
                <input type="text" v-model="editForm.phone" class="form-control" placeholder="Enter phone number" />
              </div>
              <div class="form-group modern-input">
                <label class="input-label"><i class="fas fa-image"></i> Profile Photo</label>
                <div class="edit-pic-row mb-2">
                  <img v-if="editForm.existingImageUrl" :src="editForm.existingImageUrl" class="edit-existing-pic" />
                  <div v-else class="edit-avatar-placeholder">{{ getInitials(editForm.name) }}</div>
                </div>
                <input type="file" @change="handleEditFileUpload" class="form-control-file" />
                <img v-if="editForm.previewUrl" :src="editForm.previewUrl" class="img-fluid rounded border mt-2" style="max-width:80px" />
              </div>
              <div class="form-group modern-input">
                <label class="input-label"><i class="fas fa-sticky-note"></i> Notes</label>
                <textarea class="form-control" v-model="editForm.notes" placeholder="Add any notes" rows="2"></textarea>
              </div>
              <div class="emergency-checkbox-row">
                <input type="checkbox" id="editIsEmergency" v-model="editForm.isEmergency" />
                <label for="editIsEmergency">
                  <span class="ec-title"><i class="fas fa-shield-alt"></i> Mark as emergency contact</span>
                  <span class="ec-desc">Emergency contacts can always call/message this device even when restrictions are active</span>
                </label>
              </div>
            </div>
            <div class="modal-footer cmodal-footer">
              <button type="button" class="btn btn-outline-secondary" data-dismiss="modal"><i class="fas fa-times"></i> Cancel</button>
              <button type="button" class="btn btn-primary" @click="updateContact" :disabled="isUpdating">
                <i v-if="!isUpdating" class="fas fa-check-circle"></i>
                <i v-else class="fas fa-spinner fa-spin"></i>
                {{ isUpdating ? 'Updating...' : 'Update Contact' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- DELETE MODAL -->
      <div class="modal fade" id="deleteContactModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title text-danger"><i class="fas fa-exclamation-triangle"></i> Delete Contact</h5>
              <button type="button" class="close" data-dismiss="modal"><span>&times;</span></button>
            </div>
            <div class="modal-body">Are you sure you want to delete this contact? This cannot be undone.</div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
              <button type="button" class="btn btn-danger" @click="deleteContact(selectedContactId)">Yes, Delete</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ═══════════════════════════════════════
           CONTACT REQUESTS MODAL
      ════════════════════════════════════════ -->
      <div class="modal fade" id="contactRequestsModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content cmodal-content text-left">
            <div class="modal-header cmodal-header">
              <div class="d-flex align-items-center">
                <div class="cmodal-icon indigo"><i class="fas fa-user-clock"></i></div>
                <div class="ml-2">
                  <h5 class="modal-title mb-0">Contact Requests</h5>
                  <p class="modal-subtitle" v-if="totalPendingRequests > 0">
                    {{ totalPendingRequests }} pending request{{ totalPendingRequests !== 1 ? 's' : '' }}
                  </p>
                </div>
              </div>
              <button type="button" class="close" data-dismiss="modal"><span>&times;</span></button>
            </div>

            <!-- Tab bar -->
            <div class="req-tab-bar">
              <button
                class="req-tab"
                :class="{ 'req-tab-active': requestsTab === 'add' }"
                @click="requestsTab = 'add'"
              >
                <i class="fas fa-user-plus"></i>
                Add Requests
                <span v-if="addRequests.length > 0" class="req-tab-count req-tab-count-add">
                  {{ addRequests.length }}
                </span>
              </button>
              <button
                class="req-tab"
                :class="{ 'req-tab-active': requestsTab === 'remove' }"
                @click="requestsTab = 'remove'"
              >
                <i class="fas fa-user-minus"></i>
                Remove Requests
                <span v-if="removeRequests.length > 0" class="req-tab-count req-tab-count-remove">
                  {{ removeRequests.length }}
                </span>
              </button>
            </div>

            <div class="modal-body req-modal-body" v-if="!requestsLoading">

              <!-- ADD REQUESTS TAB -->
              <div v-show="requestsTab === 'add'">
                <div v-if="addRequests.length === 0" class="req-empty">
                  <div class="req-empty-icon"><i class="fas fa-user-plus"></i></div>
                  <p class="req-empty-text">No pending add requests</p>
                  <p class="req-empty-sub">Your child hasn't requested to add any new contacts</p>
                </div>
                <div
                  v-for="req in addRequests"
                  :key="req._id"
                  class="req-card"
                >
                  <div class="req-card-left">
                    <div class="req-avatar req-avatar-add">
                      {{ getInitials(req.name) }}
                    </div>
                    <div class="req-info">
                      <p class="req-name">{{ req.name }}</p>
                      <p class="req-phone">{{ req.phone }}</p>
                      <p v-if="req.notes" class="req-notes">{{ req.notes }}</p>
                    </div>
                  </div>
                  <div class="req-actions">
                    <button
                      class="btn-req-approve"
                      @click="approveContact(req)"
                      :disabled="req._processing"
                      title="Approve — add this contact"
                    >
                      <i v-if="!req._processing" class="fas fa-check"></i>
                      <i v-else class="fas fa-spinner fa-spin"></i>
                    </button>
                    <button
                      class="btn-req-reject"
                      @click="rejectRequest(req._id)"
                      :disabled="req._processing"
                      title="Reject request"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- REMOVE REQUESTS TAB -->
              <div v-show="requestsTab === 'remove'">
                <div v-if="removeRequests.length === 0" class="req-empty">
                  <div class="req-empty-icon"><i class="fas fa-user-minus"></i></div>
                  <p class="req-empty-text">No pending remove requests</p>
                  <p class="req-empty-sub">Your child hasn't requested to remove any contacts</p>
                </div>
                <div
                  v-for="req in removeRequests"
                  :key="req._id"
                  class="req-card"
                >
                  <div class="req-card-left">
                    <div class="req-avatar req-avatar-remove">
                      {{ getInitials(req.name) }}
                    </div>
                    <div class="req-info">
                      <p class="req-name">{{ req.name }}</p>
                      <p class="req-phone">{{ req.phone }}</p>
                      <p v-if="req.notes" class="req-notes">{{ req.notes }}</p>
                    </div>
                  </div>
                  <div class="req-actions">
                    <button
                      class="btn-req-approve btn-req-approve-remove"
                      @click="approveContact(req)"
                      :disabled="req._processing"
                      title="Approve — remove this contact"
                    >
                      <i v-if="!req._processing" class="fas fa-check"></i>
                      <i v-else class="fas fa-spinner fa-spin"></i>
                    </button>
                    <button
                      class="btn-req-reject"
                      @click="rejectRequest(req._id)"
                      :disabled="req._processing"
                      title="Reject — keep this contact"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>

            </div>
            <div class="modal-body text-center py-5" v-else>
              <i class="fas fa-spinner fa-spin fa-2x text-muted"></i>
            </div>

            <div class="modal-footer cmodal-footer">
              <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">
                <i class="fas fa-times"></i> Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- SETTINGS MODAL -->
      <div class="modal fade" id="contactSettingsModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg" role="document">
          <div class="modal-content cmodal-content text-left">
            <div class="modal-header cmodal-header">
              <div class="d-flex align-items-center">
                <div class="cmodal-icon amber"><i class="fas fa-sliders-h"></i></div>
                <h5 class="modal-title ml-2">Contact &amp; Communication Settings (BETA)</h5>
              </div>
              <button type="button" class="close" data-dismiss="modal"><span>&times;</span></button>
            </div>

            <div class="modal-body settings-modal-body" v-if="settingsLoaded">

              <div v-if="!settings.enableWingioDialer" class="wingo-dialer-banner">
                <div class="wdb-icon"><i class="fas fa-phone-slash"></i></div>
                <div class="wdb-body">
                  <p class="wdb-title">Wingio Dialer is disabled</p>
                  <p class="wdb-desc">
                    Contact management settings only work when the Wingio Dialer app is enabled on
                    the child's device. Enable it below to activate call and SMS restrictions.
                  </p>
                </div>
              </div>

              <!-- Wingio Dialer row — always interactive -->
              <p class="settings-section-label settings-section-first">
                <i class="fas fa-phone-alt"></i> Wingio Dialer
              </p>
              <div class="settings-row">
                <div class="settings-info">
                  <div class="settings-title">Enable Wingio Dialer &amp; SMS app</div>
                  <div class="settings-desc">
                    The Wingio Dialer app must be active on the child's device for all contact and
                    communication restrictions below to take effect.
                  </div>
                </div>
                <label class="toggle-switch">
                  <input type="checkbox" v-model="settings.enableWingioDialer" /><span class="toggle-track"></span>
                </label>
              </div>

              <!-- All remaining settings locked when dialer off -->
              <div :class="{ 'settings-lockout': !settings.enableWingioDialer }">

                <p class="settings-section-label"><i class="fas fa-phone"></i> Calls</p>
                <div class="settings-row">
                  <div class="settings-info">
                    <div class="settings-title">Allow all incoming calls <span class="badge-not-recommended">Not recommended</span></div>
                    <div class="settings-desc">Disabling restricts incoming calls to approved contacts only</div>
                  </div>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="settings.allowAllIncomingCall" :disabled="!settings.enableWingioDialer" /><span class="toggle-track"></span>
                  </label>
                </div>
                <div class="settings-row">
                  <div class="settings-info">
                    <div class="settings-title">Allow all outgoing calls <span class="badge-not-recommended">Not recommended</span></div>
                    <div class="settings-desc">Disabling restricts outgoing calls to approved contacts only</div>
                  </div>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="settings.allowAllOutgoingCall" :disabled="!settings.enableWingioDialer" /><span class="toggle-track"></span>
                  </label>
                </div>

                <p class="settings-section-label"><i class="fas fa-sms"></i> SMS</p>
                <div class="settings-row">
                  <div class="settings-info">
                    <div class="settings-title">Allow all incoming SMS <span class="badge-not-recommended">Not recommended</span></div>
                    <div class="settings-desc">Disabling restricts incoming messages to approved contacts only</div>
                  </div>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="settings.allowAllIncomingSms" :disabled="!settings.enableWingioDialer" /><span class="toggle-track"></span>
                  </label>
                </div>
                <div class="settings-row">
                  <div class="settings-info">
                    <div class="settings-title">Allow all outgoing SMS <span class="badge-not-recommended">Not recommended</span></div>
                    <div class="settings-desc">Disabling restricts outgoing messages to approved contacts only</div>
                  </div>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="settings.allowAllOutgoingSms" :disabled="!settings.enableWingioDialer" /><span class="toggle-track"></span>
                  </label>
                </div>

                <p class="settings-section-label"><i class="fas fa-user-check"></i> Contact Requests</p>
                <div class="settings-row">
                  <div class="settings-info">
                    <div class="settings-title">Auto-approve contact requests</div>
                    <div class="settings-desc">Automatically approve or remove contacts requested by the child without parent review</div>
                  </div>
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="settings.autoApproveContacts" :disabled="!settings.enableWingioDialer" /><span class="toggle-track"></span>
                  </label>
                </div>

                <p class="settings-section-label"><i class="fas fa-history"></i> History &amp; Tracking</p>
                <div class="settings-row">
                  <div class="settings-info">
                    <div class="settings-title">
                      Enable call history tracking
                      <span v-if="!hasPremium" class="badge-premium"><i class="fas fa-star"></i> Premium only</span>
                    </div>
                    <div class="settings-desc">When enabled, incoming and outgoing call logs are tracked and visible to parents</div>
                  </div>
                  <label class="toggle-switch" :class="{ 'toggle-disabled': !hasPremium }">
                    <input type="checkbox" v-model="settings.enableCallHistory" :disabled="!hasPremium || !settings.enableWingioDialer" /><span class="toggle-track"></span>
                  </label>
                </div>
                <div class="settings-row">
                  <div class="settings-info">
                    <div class="settings-title">
                      Enable SMS history tracking
                      <span v-if="!hasPremium" class="badge-premium"><i class="fas fa-star"></i> Premium only</span>
                      <span v-if="hasPremium && settings.disableWingioSms" class="badge-blocked"><i class="fas fa-lock"></i> Requires Wingio SMS</span>
                    </div>
                    <div class="settings-desc">When enabled, SMS messages are tracked and visible to parents. Requires Wingio SMS to be active.</div>
                  </div>
                  <label class="toggle-switch" :class="{ 'toggle-disabled': !hasPremium || settings.disableWingioSms }">
                    <input type="checkbox" v-model="settings.enableSmsHistory" :disabled="!hasPremium || settings.disableWingioSms || !settings.enableWingioDialer" /><span class="toggle-track"></span>
                  </label>
                </div>

                <p class="settings-section-label"><i class="fas fa-comment-alt"></i> Wingio SMS</p>
                <div class="settings-row settings-row-last">
                  <div class="settings-info">
                    <div class="settings-title">Use Wingio SMS app</div>
                    <div class="settings-desc">
                      Wingio SMS enables call and message monitoring on the child's device.
                      <span v-if="settings.disableWingioSms" class="wingo-warning">
                        <i class="fas fa-exclamation-triangle"></i>
                        SMS activity cannot be traced or recorded when Wingio is disabled.
                        Make sure an alternative messaging app is installed on the device.
                      </span>
                    </div>
                  </div>
                  <label class="toggle-switch">
                    <input type="checkbox" :checked="!settings.disableWingioSms" @change="onWingioToggle" :disabled="!settings.enableWingioDialer" /><span class="toggle-track"></span>
                  </label>
                </div>

              </div>
              <!-- /settings-lockout -->

            </div>
            <div class="modal-body text-center py-4" v-else>
              <i class="fas fa-spinner fa-spin fa-2x text-muted"></i>
            </div>

            <div class="modal-footer cmodal-footer">
              <button type="button" class="btn btn-outline-secondary" data-dismiss="modal"><i class="fas fa-times"></i> Cancel</button>
              <button type="button" class="btn btn-primary" @click="saveSettings" :disabled="isSavingSettings">
                <i v-if="!isSavingSettings" class="fas fa-save"></i>
                <i v-else class="fas fa-spinner fa-spin"></i>
                {{ isSavingSettings ? 'Saving...' : 'Save Settings' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- COPY CONTACTS MODAL -->
      <div class="modal fade" id="copyContactsModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content cmodal-content text-left">
            <div class="modal-header cmodal-header">
              <div class="d-flex align-items-center">
                <div class="cmodal-icon teal"><i class="fas fa-copy"></i></div>
                <h5 class="modal-title ml-2">Copy Contacts to Another Device</h5>
              </div>
              <button type="button" class="close" data-dismiss="modal"><span>&times;</span></button>
            </div>
            <div class="modal-body cmodal-body">
              <div class="copy-devices-row">
                <div class="copy-device-select">
                  <label class="input-label"><i class="fas fa-mobile-alt"></i> From (source)</label>
                  <select class="form-control" v-model="copyForm.fromProfileId">
                    <option value="">Select source device</option>
                    <option v-for="d in allDevices" :key="d.profileId" :value="d.profileId">{{ d.label }}</option>
                  </select>
                </div>
                <div class="copy-arrow"><i class="fas fa-arrow-right"></i></div>
                <div class="copy-device-select">
                  <label class="input-label"><i class="fas fa-mobile-alt"></i> To (destination)</label>
                  <select class="form-control" v-model="copyForm.toProfileId">
                    <option value="">Select destination device</option>
                    <option v-for="d in allDevices.filter(d => d.profileId !== copyForm.fromProfileId)" :key="d.profileId" :value="d.profileId">{{ d.label }}</option>
                  </select>
                </div>
              </div>
              <div class="copy-info-box mt-3">
                <i class="fas fa-info-circle"></i>
                All contacts are copied to the destination. Duplicates with the same phone number are skipped.
              </div>
            </div>
            <div class="modal-footer cmodal-footer">
              <button type="button" class="btn btn-outline-secondary" data-dismiss="modal"><i class="fas fa-times"></i> Cancel</button>
              <button
                type="button"
                class="btn btn-primary"
                @click="copyContacts"
                :disabled="isCopying || !copyForm.fromProfileId || !copyForm.toProfileId"
              >
                <i v-if="!isCopying" class="fas fa-copy"></i>
                <i v-else class="fas fa-spinner fa-spin"></i>
                {{ isCopying ? 'Copying...' : 'Copy All Contacts' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
    </div>
  </div>
</template>

<script>
import KidHeader from "@/components/KidHeader.vue";
import KidFooter from "@/components/KidFooter.vue";
import ProfileService from "../../services/ProfileService";
import KidsService from "../../services/KidsService";
export default {
  name: "Contacts",
  components: { KidHeader, KidFooter },

  computed: {
    totalPendingRequests() {
      return this.addRequests.length + this.removeRequests.length;
    },
    isContactPickerSupported() {
      return (
        'contacts' in navigator &&
        'select' in navigator.contacts
      );
    }
  },

  data() {
    return {
      profileId: this.$route.query.profileId,
      serialNumber: this.$route.query.serialNumber,

      contacts: [],
      selectedContactId: null,
      hasChatAccess: false,
      hasPremium: false,

      isAdding: false,
      isUpdating: false,
      isDisabled: false,
      isSavingSettings: false,
      isCopying: false,
      importing: false,
      settingsLoaded: false,
      wingioDialerLoaded: false,

      // Contact requests
      addRequests:     [],  // type: 'add'
      removeRequests:  [],  // type: 'remove'
      requestsLoading: false,
      requestsTab:     'add',

      addForm: {
        name: "", phone: "", notes: "", isEmergency: false, imagePayload: null, previewUrl: null,
      },
      editForm: {
        id: null, name: "", phone: "", notes: "", isEmergency: false,
        existingImageUrl: "", imagePayload: null, previewUrl: null,
      },

      settings: {
        enableWingioDialer:  true,
        allowAllIncomingCall: false,
        allowAllOutgoingCall: false,
        allowAllIncomingSms:  false,
        allowAllOutgoingSms:  false,
        autoApproveContacts:  false,
        enableCallHistory:    false,
        enableSmsHistory:     false,
        disableWingioSms:     false,
      },

      copyForm: { fromProfileId: "", toProfileId: "" },
      allDevices: [],
    };
  },

  methods: {
    // ─── File handlers ────────────────────────────────────────────
    handleAddFileUpload(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.addForm.imagePayload = file;
      this.addForm.previewUrl = URL.createObjectURL(file);
    },
    handleEditFileUpload(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.editForm.imagePayload = file;
      this.editForm.previewUrl = URL.createObjectURL(file);
    },

    // ─── Modal openers ────────────────────────────────────────────
    openAddModal() {
      this.addForm = { name: "", phone: "", notes: "", isEmergency: false, imagePayload: null, previewUrl: null };
      $("#addContactModal").modal("show");
    },
    openEditModal(contact) {
      this.editForm = {
        id: contact._id, name: contact.name || "", phone: contact.phone || "",
        notes: contact.notes || "", isEmergency: contact.isEmergency || false,
        existingImageUrl: contact.imageUrl || "", imagePayload: null, previewUrl: null,
      };
      $("#editContactModal").modal("show");
    },
    openDeleteModal(id) {
      this.selectedContactId = id;
      $("#deleteContactModal").modal("show");
    },
    async openSettingsModal() {
      this.settingsLoaded = false;
      $("#contactSettingsModal").modal("show");
      await this.loadSettings();
    },
    async openCopyModal() {
      await this.loadAllDevices();
      this.copyForm = { fromProfileId: this.profileId, toProfileId: "" };
      $("#copyContactsModal").modal("show");
    },
    async openRequestsModal() {
      this.requestsTab = 'add';
      $("#contactRequestsModal").modal("show");
      await this.loadContactRequests();
    },

    // ─── Contacts CRUD ────────────────────────────────────────────
    async getContacts() {
      try {
        const res = await ProfileService.listContact({ profileId: this.profileId, serialNumber: this.serialNumber });
        this.contacts = res.data.data;
        this.hasChatAccess = res.data.hasChatAccess;
        this.hasPremium = res.data.hasPremium || false;
      } catch (e) {
        this.$toast.error(e.response?.data?.respones_msg || "Failed to load contacts");
      }
    },

    async addContact() {
      if (!this.addForm.name || !this.addForm.phone) { this.$toast.error("Name and phone number are required."); return; }
      this.isAdding = true;
      const fd = new FormData();
      fd.append("profile_id", this.profileId);
      fd.append("serialNumber", this.serialNumber);
      fd.append("name", this.addForm.name);
      fd.append("phone", this.addForm.phone);
      fd.append("notes", this.addForm.notes);
      fd.append("isEmergency", this.addForm.isEmergency);
      if (this.addForm.imagePayload) fd.append("image", this.addForm.imagePayload);
      try {
        const res = await ProfileService.addContact(fd);
        this.$toast.success(res.data.respones_msg);
        $("#addContactModal").modal("hide");
        this.getContacts();
        this.applyPolicy();
      } catch (e) {
        this.$toast.error(e.response?.data?.respones_msg || "Failed to add contact");
      } finally { this.isAdding = false; }
    },

    async updateContact() {
      if (!this.editForm.name || !this.editForm.phone) { this.$toast.error("Name and phone number are required."); return; }
      this.isUpdating = true;
      const fd = new FormData();
      fd.append("id", this.editForm.id);
      fd.append("profile_id", this.profileId);
      fd.append("serialNumber", this.serialNumber);
      fd.append("name", this.editForm.name);
      fd.append("phone", this.editForm.phone);
      fd.append("notes", this.editForm.notes);
      fd.append("isEmergency", this.editForm.isEmergency);
      if (this.editForm.imagePayload) fd.append("image", this.editForm.imagePayload);
      try {
        const res = await ProfileService.addContact(fd);
        this.$toast.success(res.data.respones_msg);
        $("#editContactModal").modal("hide");
        this.getContacts();
        this.applyPolicy();
      } catch (e) {
        this.$toast.error(e.response?.data?.respones_msg || "Failed to update contact");
      } finally { this.isUpdating = false; }
    },

    async deleteContact(id) {
      $("#deleteContactModal").modal("hide");
      try {
        const res = await ProfileService.deleteContact({ id, serialNumber: this.serialNumber });
        this.$toast.success(res.data.respones_msg);
        this.getContacts();
        this.applyPolicy();
      } catch (e) {
        this.$toast.error(e.response?.data?.respones_msg || "Failed to delete contact");
      }
    },

    // ─── Contact Requests ─────────────────────────────────────────
    async loadContactRequests() {
      this.requestsLoading = true;
      try {
        const res = await ProfileService.listContactRequests({
          profileId: this.profileId,
          serialNumber: this.serialNumber,
        });
        // Backend returns { addRequests: [...], removeRequests: [...] }
        this.addRequests    = (res.data.addRequests    || []).map(r => ({ ...r, _processing: false }));
        this.removeRequests = (res.data.removeRequests || []).map(r => ({ ...r, _processing: false }));
        // Auto-switch tab to whichever has items, preferring add
        if (this.addRequests.length === 0 && this.removeRequests.length > 0) {
          this.requestsTab = 'remove';
        }
      } catch (e) {
        this.$toast.error("Failed to load contact requests");
      } finally {
        this.requestsLoading = false;
      }
    },

    // Reject any request type — just marks it as rejected
    async rejectRequest(req) {
      req._processing = true;
      try {
        await ProfileService.resolveContactRequest({ id: req._id, action: 'reject' });
        this.$toast.success("Request rejected");
        this.addRequests    = this.addRequests.filter(r => r._id !== req._id);
        this.removeRequests = this.removeRequests.filter(r => r._id !== req._id);
      } catch (e) {
        this.$toast.error(e.response?.data?.respones_msg || "Failed to reject request");
        req._processing = false;
      }
    },

    // ─── Settings ─────────────────────────────────────────────────
    async loadSettings() {
      try {
        const res = await ProfileService.getContactSettings({ profileId: this.profileId, serialNumber: this.serialNumber });
        this.settings = { ...this.settings, ...res.data.data };
        this.settingsLoaded = true;
      } catch (e) {
        this.$toast.error("Failed to load settings");
        this.settingsLoaded = true;
      }
    },

    async saveSettings() {
      this.isSavingSettings = true;
      try {
        const res = await ProfileService.updateContactSettings({
          profileId: this.profileId,
          serialNumber: this.serialNumber,
          ...this.settings,
        });
        this.$toast.success(res.data.respones_msg);
        $("#contactSettingsModal").modal("hide");
        this.applyPolicy();
      } catch (e) {
        this.$toast.error(e.response?.data?.respones_msg || "Failed to save settings");
      } finally { this.isSavingSettings = false; }
    },

    onWingioToggle(e) {
      this.settings.disableWingioSms = !e.target.checked;
      if (this.settings.disableWingioSms) this.settings.enableSmsHistory = false;
    },

    async loadWingioDialerStatus() {
      try {
        const res = await ProfileService.getContactSettings({
          profileId: this.profileId,
          serialNumber: this.serialNumber,
        });
        this.settings.enableWingioDialer = res.data.data.enableWingioDialer ?? true;
      } catch (_) {
        // non-fatal
      } finally {
        this.wingioDialerLoaded = true;
      }
    },

    // ─── Copy contacts ─────────────────────────────────────────────
    async loadAllDevices() {
      try {
        const res = await ProfileService.getDeviceList({ created_by: this.$store.state.userId });
        this.allDevices = (res.data.data || []).map((d) => ({
          profileId: d.profile_id,
          label: d.name || d.serialNumber || d.profile_id,
        }));
      } catch (e) { this.$toast.error("Failed to load devices"); }
    },

    async copyContacts() {
      if (!this.copyForm.fromProfileId || !this.copyForm.toProfileId) return;
      this.isCopying = true;
      try {
        const res = await ProfileService.copyContacts({ fromProfileId: this.copyForm.fromProfileId, toProfileId: this.copyForm.toProfileId });
        this.$toast.success(res.data.respones_msg);
        $("#copyContactsModal").modal("hide");
        this.getContacts();
      } catch (e) {
        this.$toast.error(e.response?.data?.respones_msg || "Failed to copy contacts");
      } finally { this.isCopying = false; }
    },

    // ─── Helpers ───────────────────────────────────────────────────
    applyPolicy() {
      this.isDisabled = true;
      ProfileService.applyPolicy({ profile_id: this.profileId, serialNumber: this.serialNumber })
        .then(() => { this.isDisabled = false; })
        .catch(() => { this.isDisabled = false; });
    },

    getInitials(name) {
      if (!name) return "?";
      const parts = name.trim().split(" ");
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return name.substring(0, 2).toUpperCase();
    },

    async rejectRequest(id) {
        let data = { profileId: this.profileId, serialNumber: this.serialNumber, id: id };
        await KidsService.rejectRequest(data)
        .then((response) => {
          $("#contactRequestsModal").modal("hide");
          this.getContacts();
            ProfileService.listContactRequests({ profileId: this.$route.query.profileId, serialNumber: this.$route.query.serialNumber })
          .then(res => {
            this.addRequests    = (res.data.addRequests    || []).map(r => ({ ...r, _processing: false }));
            this.removeRequests = (res.data.removeRequests || []).map(r => ({ ...r, _processing: false }));
          })
          .catch(() => {});
          this.$toast.success(response.data.respones_msg);
        }).catch((e) => {
          this.$toast.error(e.response.data.respones_msg);
        });
    },
    approveContact(item) {
        let data = { profileId: this.profileId, serialNumber: this.serialNumber, id: item._id };
         KidsService.approveContact(data)
        .then((response) => {
          $("#contactRequestsModal").modal("hide");
          this.getContacts();
            this.applyPolicy();
            ProfileService.listContactRequests({ profileId: this.$route.query.profileId, serialNumber: this.$route.query.serialNumber })
          .then(res => {
            this.addRequests    = (res.data.addRequests    || []).map(r => ({ ...r, _processing: false }));
            this.removeRequests = (res.data.removeRequests || []).map(r => ({ ...r, _processing: false }));
          })
          .catch(() => {});
          this.$toast.success(response.data.respones_msg);
        }).catch((e) => {
          this.$toast.error(e.response.data.respones_msg);
        });
    },
    async importFromPhone() {
      if (!this.isContactPickerSupported) {
        this.$toast.error('Contact import not supported on this device');
        return;
      }
      this.importResults = { success: [], failed: [] };
      try {
        const selectedContacts = await navigator.contacts.select(
          ['name', 'tel', 'icon', 'email'],
          { multiple: true }
        );
        if (!selectedContacts.length) {
          this.$toast.info('No contacts selected');
          return;
        }
        this.importing = true;
        for (const contact of selectedContacts) {
          const name = contact.name?.[0];
          const phone = contact.tel?.[0];
          const email = contact.email?.[0];
          if (!name || !phone) {
            this.importResults.failed.push({
              name: name || 'Unknown',
              reason: 'Missing name or phone number'
            });
            continue;
          }
          try {
            await this.addImportedContact(name, phone, email);
            this.importResults.success.push(name);
          } catch (err) {
            this.importResults.failed.push({
              name,
              reason: err?.response?.data?.respones_msg || 'Failed to add contact'
            });
          }
        }
        this.importing = false;
        this.getContacts();
        this.applyPolicy();
        this.showImportSummary();
      } catch (err) {
        this.$toast.error(err.message || 'Failed to import contacts');
      }
    },

    async addImportedContact(name, phone, email) {
      const formData = new FormData();
      formData.append('profile_id', this.profileId);
      formData.append('serialNumber', this.serialNumber);
      formData.append('name', name);
      formData.append('phone', phone);
      formData.append('email', email);
      formData.append('notes', "");
      return await ProfileService.addContact(formData);
    },

    showImportSummary() {
      const successCount = this.importResults.success.length;
      const failedCount = this.importResults.failed.length;
      if (successCount) {
        this.$toast.success(`Imported ${successCount} contact(s) successfully`);
      }
      if (failedCount) {
        const messages = this.importResults.failed
          .map(f => `${f.name}: ${f.reason}`)
          .join('<br>');
        this.$toast.error(
          `Failed to import ${failedCount} contact(s)<br>${messages}`,
          { dangerouslyUseHTMLString: true }
        );
      }
    }
  },

  beforeMount() {
    this.getContacts();
    this.loadWingioDialerStatus();
    // Also pre-load request count so the badge is visible immediately
    ProfileService.listContactRequests({ profileId: this.$route.query.profileId, serialNumber: this.$route.query.serialNumber })
      .then(res => {
        this.addRequests    = (res.data.addRequests    || []).map(r => ({ ...r, _processing: false }));
        this.removeRequests = (res.data.removeRequests || []).map(r => ({ ...r, _processing: false }));
      })
      .catch(() => {});
  },
};
</script>

<style scoped>
/* ── Page-level Wingio Dialer disabled banner ────────────── */
.page-dialer-banner {
  display:flex; align-items:flex-start; gap:12px;
  background:#fef2f2; border:1px solid #fecaca;
  border-radius:12px; padding:14px 16px;
}
.pdb-icon {
  width:40px; height:40px; border-radius:10px; flex-shrink:0;
  background:#fee2e2; color:#dc2626;
  display:flex; align-items:center; justify-content:center; font-size:1.1rem;
}
.pdb-body { flex:1; min-width:0; }
.pdb-title { font-size:.9rem; font-weight:700; color:#991b1b; margin:0 0 4px; }
.pdb-desc  { font-size:.78rem; color:#b91c1c; line-height:1.5; margin:0; }
.pdb-link  { color:#991b1b; font-weight:600; text-decoration:underline; }
.pdb-link:hover { color:#7f1d1d; }

/* Greyed-out contacts card when Wingio Dialer is off */
.page-contacts-lockout {
  opacity: 0.45;
  pointer-events: none;
  user-select: none;
}

/* ── Top action bar ─────────────────────────────────────── */
.top-action-bar { background:#fff; border:1px solid #e2e8f0; border-radius:12px; padding:12px 16px; }
.action-btn { border-radius:8px; font-size:13px; display:inline-flex; align-items:center; gap:6px; }
.gap-2 { gap:8px; }

/* Requests button + badge */
.request-btn { position:relative; }
.request-badge {
  display:inline-flex; align-items:center; justify-content:center;
  min-width:18px; height:18px; padding:0 5px;
  border-radius:100px; background:#ef4444; color:#fff;
  font-size:.65rem; font-weight:700; line-height:1;
  margin-left:2px;
}

/* ── Contacts card ──────────────────────────────────────── */
.contacts-card { background:#fff; border-radius:16px; box-shadow:0 2px 8px rgba(0,0,0,.06); border:1px solid #e2e8f0; overflow:hidden; }
.contacts-header { display:flex; align-items:center; padding:1rem 1.25rem; background:linear-gradient(135deg,#f8fafc,#f1f5f9); border-bottom:2px solid #e2e8f0; }
.header-left { display:flex; align-items:center; gap:.875rem; }
.contacts-icon { width:40px; height:40px; border-radius:10px; background:linear-gradient(135deg,#6366f1,#4f46e5); display:flex; align-items:center; justify-content:center; color:#fff; font-size:1.1rem; }
.contacts-title { font-size:1.1rem; font-weight:700; color:#1e293b; margin:0 0 2px; }
.contacts-count { font-size:.8rem; color:#64748b; margin:0; }

/* ── Table ──────────────────────────────────────────────── */
.contacts-table { margin:0; width:100%; }
.contacts-table thead th { font-size:.78rem; font-weight:700; color:#475569; text-transform:uppercase; letter-spacing:.025em; padding:.75rem 1rem; border-bottom:2px solid #e2e8f0; background:#f8fafc; white-space:nowrap; }
.contacts-table tbody tr.contact-row { transition:background .15s; border-bottom:1px solid #f1f5f9; }
.contacts-table tbody tr.contact-row:hover { background:#f8fafc; }
.contacts-table tbody td { padding:.875rem 1rem; vertical-align:middle; font-size:.875rem; }
.emergency-row { background:#fffbeb !important; }
.emergency-row:hover { background:#fef3c7 !important; }

/* ── Avatar ─────────────────────────────────────────────── */
.name-wrapper { display:flex; align-items:center; gap:.625rem; }
.avatar { width:36px; height:36px; border-radius:9px; flex-shrink:0; background:linear-gradient(135deg,#dbeafe,#bfdbfe); color:#1e40af; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:.78rem; overflow:hidden; }
.avatar-img { width:100%; height:100%; object-fit:cover; }
.name-info { display:flex; flex-direction:column; gap:2px; }
.name-row { display:flex; align-items:center; gap:5px; flex-wrap:wrap; }
.name { font-weight:600; color:#1e293b; font-size:.875rem; }
.email { font-size:.75rem; color:#64748b; }
.phone-link { color:#2563eb; text-decoration:none; font-weight:500; font-size:.875rem; }
.phone-link:hover { color:#1d4ed8; }
.phone-notes { font-size:.72rem; color:#6b7280; margin-top:2px; }

/* ── Emergency pill (inline next to name) ───────────────── */
.emergency-pill {
  display:inline-flex; align-items:center; gap:3px;
  padding:2px 7px; border-radius:100px;
  background:#fee2e2; color:#991b1b;
  font-size:.68rem; font-weight:600; white-space:nowrap; line-height:1.4;
}

/* ── Action buttons ─────────────────────────────────────── */
.contact-actions { display:flex; gap:6px; justify-content:center; }
.btn-edit { width:30px; height:30px; border-radius:8px; border:none; background:#eff6ff; color:#2563eb; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:all .2s; font-size:.8rem; }
.btn-edit:hover { background:#2563eb; color:#fff; transform:scale(1.1); }
.btn-delete { width:30px; height:30px; border-radius:8px; border:none; background:#fee2e2; color:#dc2626; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:all .2s; font-size:.8rem; }
.btn-delete:hover { background:#dc2626; color:#fff; transform:scale(1.1); }

/* ── Empty state ────────────────────────────────────────── */
.empty-state { text-align:center; padding:2.5rem 1.5rem !important; }
.empty-icon { width:64px; height:64px; margin:0 auto .875rem; background:linear-gradient(135deg,#f1f5f9,#e2e8f0); border-radius:16px; display:flex; align-items:center; justify-content:center; color:#94a3b8; font-size:1.5rem; }
.empty-text { font-size:.95rem; font-weight:600; color:#475569; margin:0 0 .375rem; }
.empty-subtext { font-size:.82rem; color:#94a3b8; margin:0; }

/* ── Modal shared ───────────────────────────────────────── */
.cmodal-content { border-radius:16px; border:none; box-shadow:0 20px 60px rgba(0,0,0,.15); overflow:hidden; }
.cmodal-header { padding:1rem 1.25rem; border-bottom:1px solid #e2e8f0; background:linear-gradient(135deg,#f8fafc,#f1f5f9); flex-shrink:0; }
.cmodal-header .modal-title { font-size:1rem; font-weight:700; color:#1e293b; }
.modal-subtitle { font-size:.75rem; color:#64748b; margin:1px 0 0; }
.cmodal-body { padding:1.25rem; display:flex; flex-direction:column; gap:.75rem; }
.cmodal-footer { padding:.875rem 1.25rem; border-top:1px solid #f1f5f9; display:flex; justify-content:flex-end; gap:.625rem; flex-shrink:0; }

/* Settings modal scroll fix */
#contactSettingsModal .modal-dialog { max-height:90vh; }
#contactSettingsModal .modal-content { max-height:90vh; display:flex; flex-direction:column; }
#contactSettingsModal .settings-modal-body { flex:1 1 auto; min-height:0; overflow-y:auto; padding:1rem 1.25rem; }

/* Requests modal scroll fix */
#contactRequestsModal .modal-dialog { max-height:90vh; }
#contactRequestsModal .modal-content { max-height:90vh; display:flex; flex-direction:column; }
#contactRequestsModal .req-modal-body { flex:1 1 auto; min-height:0; overflow-y:auto; padding:.75rem 1.25rem; }

/* ── Modal icon variants ────────────────────────────────── */
.cmodal-icon { width:36px; height:36px; border-radius:9px; display:flex; align-items:center; justify-content:center; color:#fff; font-size:.95rem; flex-shrink:0; }
.cmodal-icon.blue   { background:linear-gradient(135deg,#3b82f6,#2563eb); }
.cmodal-icon.purple { background:linear-gradient(135deg,#8b5cf6,#7c3aed); }
.cmodal-icon.amber  { background:linear-gradient(135deg,#f59e0b,#d97706); }
.cmodal-icon.teal   { background:linear-gradient(135deg,#14b8a6,#0d9488); }
.cmodal-icon.indigo { background:linear-gradient(135deg,#6366f1,#4f46e5); }

/* ── Requests tab bar ───────────────────────────────────── */
.req-tab-bar {
  display:flex; border-bottom:2px solid #f1f5f9;
  background:#fff; flex-shrink:0;
}
.req-tab {
  flex:1; padding:11px 12px; border:none; background:none;
  font-size:.82rem; font-weight:600; color:#64748b;
  display:inline-flex; align-items:center; justify-content:center; gap:6px;
  cursor:pointer; transition:all .15s;
  border-bottom:2px solid transparent; margin-bottom:-2px;
}
.req-tab:hover { color:#4f46e5; background:#f8fafc; }
.req-tab-active { color:#4f46e5; border-bottom-color:#4f46e5; }
.req-tab-count {
  display:inline-flex; align-items:center; justify-content:center;
  min-width:18px; height:18px; padding:0 5px;
  border-radius:100px; font-size:.65rem; font-weight:700; line-height:1;
}
.req-tab-count-add    { background:#dcfce7; color:#166534; }
.req-tab-count-remove { background:#fee2e2; color:#991b1b; }

/* ── Request card ───────────────────────────────────────── */
.req-card {
  display:flex; align-items:center; justify-content:space-between;
  padding:12px 0; border-bottom:1px solid #f1f5f9; gap:10px;
}
.req-card:last-child { border-bottom:none; }
.req-card-left { display:flex; align-items:center; gap:10px; flex:1; min-width:0; }
.req-avatar {
  width:38px; height:38px; border-radius:10px; flex-shrink:0;
  display:flex; align-items:center; justify-content:center;
  font-weight:700; font-size:.8rem;
}
.req-avatar-add    { background:#dcfce7; color:#166534; }
.req-avatar-remove { background:#fee2e2; color:#991b1b; }
.req-info { flex:1; min-width:0; }
.req-name  { font-size:.875rem; font-weight:600; color:#1e293b; margin:0 0 2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.req-phone { font-size:.78rem; color:#2563eb; margin:0 0 1px; }
.req-notes { font-size:.72rem; color:#64748b; margin:0; }
.req-actions { display:flex; gap:6px; flex-shrink:0; }

/* Approve (green) */
.btn-req-approve {
  width:32px; height:32px; border-radius:8px; border:none;
  background:#dcfce7; color:#16a34a;
  display:inline-flex; align-items:center; justify-content:center;
  cursor:pointer; font-size:.8rem; transition:all .2s;
}
.btn-req-approve:hover:not(:disabled) { background:#16a34a; color:#fff; transform:scale(1.08); }
/* Red variant for approve-remove (approving a removal) */
.btn-req-approve-remove { background:#fee2e2; color:#dc2626; }
.btn-req-approve-remove:hover:not(:disabled) { background:#dc2626; color:#fff; }

/* Reject (grey) */
.btn-req-reject {
  width:32px; height:32px; border-radius:8px; border:none;
  background:#f1f5f9; color:#64748b;
  display:inline-flex; align-items:center; justify-content:center;
  cursor:pointer; font-size:.8rem; transition:all .2s;
}
.btn-req-reject:hover:not(:disabled) { background:#64748b; color:#fff; transform:scale(1.08); }

.btn-req-approve:disabled,
.btn-req-reject:disabled { opacity:.5; cursor:not-allowed; transform:none; }

/* ── Request empty state ────────────────────────────────── */
.req-empty { text-align:center; padding:2rem 1rem; }
.req-empty-icon {
  width:52px; height:52px; margin:0 auto .75rem;
  background:#f1f5f9; border-radius:14px;
  display:flex; align-items:center; justify-content:center;
  color:#94a3b8; font-size:1.25rem;
}
.req-empty-text { font-size:.9rem; font-weight:600; color:#475569; margin:0 0 .25rem; }
.req-empty-sub  { font-size:.78rem; color:#94a3b8; margin:0; }

/* ── Modern input ───────────────────────────────────────── */
.modern-input { margin:0; }
.input-label { display:flex; align-items:center; gap:.375rem; font-size:.78rem; font-weight:600; color:#475569; margin-bottom:.375rem; }
.modern-input .form-control { border:2px solid #e2e8f0; border-radius:9px; padding:.6rem .875rem; font-size:.875rem; background:#f8fafc; transition:all .2s; }
.modern-input .form-control:focus { border-color:#3b82f6; background:#fff; box-shadow:0 0 0 3px rgba(59,130,246,.1); outline:none; }
.modern-input textarea.form-control { resize:vertical; min-height:60px; }

/* ── Emergency checkbox ─────────────────────────────────── */
.emergency-checkbox-row { display:flex; align-items:flex-start; gap:9px; background:#fffbeb; border:1px solid #fde68a; border-radius:9px; padding:10px; }
.emergency-checkbox-row input[type=checkbox] { margin-top:2px; flex-shrink:0; width:14px; height:14px; }
.emergency-checkbox-row label { cursor:pointer; margin:0; }
.ec-title { display:block; font-size:.84rem; font-weight:600; color:#92400e; }
.ec-desc  { display:block; font-size:.75rem; color:#b45309; margin-top:2px; line-height:1.4; }

/* ── Wingio Dialer disabled banner (settings modal) ──────── */
.wingo-dialer-banner { display:flex; align-items:flex-start; gap:12px; background:#fef2f2; border:1px solid #fecaca; border-radius:10px; padding:14px; margin-bottom:4px; }
.wdb-icon { width:36px; height:36px; border-radius:9px; flex-shrink:0; background:#fee2e2; color:#dc2626; display:flex; align-items:center; justify-content:center; font-size:1rem; }
.wdb-body { flex:1; min-width:0; }
.wdb-title { font-size:.875rem; font-weight:700; color:#991b1b; margin:0 0 4px; }
.wdb-desc  { font-size:.78rem; color:#b91c1c; line-height:1.5; margin:0; }

/* ── Settings lockout ───────────────────────────────────── */
.settings-lockout { opacity:0.45; pointer-events:none; user-select:none; position:relative; }

/* ── Settings rows ──────────────────────────────────────── */
.settings-section-label { font-size:.72rem; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.05em; margin:0; padding:12px 0 6px; border-top:1px solid #f1f5f9; display:flex; align-items:center; gap:.375rem; }
.settings-section-first { border-top:none; padding-top:0; }
.settings-row { display:flex; align-items:center; justify-content:space-between; padding:10px 0; border-bottom:1px solid #f1f5f9; gap:12px; }
.settings-row-last { border-bottom:none; }
.settings-info { flex:1; min-width:0; }
.settings-title { font-size:.84rem; font-weight:600; color:#1e293b; display:flex; align-items:center; gap:.35rem; flex-wrap:wrap; line-height:1.3; }
.settings-desc { font-size:.75rem; color:#64748b; margin-top:3px; line-height:1.4; }

.badge-not-recommended { display:inline-flex; padding:2px 6px; border-radius:100px; background:#fef3c7; color:#92400e; font-size:.68rem; font-weight:600; white-space:nowrap; flex-shrink:0; }
.badge-premium         { display:inline-flex; align-items:center; gap:3px; padding:2px 6px; border-radius:100px; background:#fef9c3; color:#854d0e; font-size:.68rem; font-weight:600; white-space:nowrap; flex-shrink:0; }
.badge-blocked         { display:inline-flex; align-items:center; gap:3px; padding:2px 6px; border-radius:100px; background:#fee2e2; color:#991b1b; font-size:.68rem; font-weight:600; white-space:nowrap; flex-shrink:0; }

.wingo-warning { display:block; margin-top:5px; padding:7px 9px; background:#fef2f2; border:1px solid #fecaca; border-radius:7px; font-size:.75rem; color:#b91c1c; line-height:1.4; }

/* ── Toggle switch ──────────────────────────────────────── */
.toggle-switch { position:relative; width:44px; height:24px; flex-shrink:0; margin:0; cursor:pointer; }
.toggle-switch input { opacity:0; width:0; height:0; }
.toggle-track { position:absolute; inset:0; background:#cbd5e1; border-radius:100px; transition:.2s; }
.toggle-track:before { content:''; position:absolute; width:18px; height:18px; left:3px; top:3px; background:#fff; border-radius:50%; transition:.2s; }
.toggle-switch input:checked + .toggle-track { background:#2563eb; }
.toggle-switch input:checked + .toggle-track:before { transform:translateX(20px); }
.toggle-switch input:disabled + .toggle-track { opacity:.4; cursor:not-allowed; }
.toggle-disabled { cursor:not-allowed; }

/* ── Copy modal ─────────────────────────────────────────── */
.copy-devices-row { display:flex; align-items:flex-end; gap:.625rem; }
.copy-device-select { flex:1; }
.copy-arrow { padding:0 4px 8px; color:#94a3b8; font-size:1rem; flex-shrink:0; }
.copy-info-box { padding:9px 11px; background:#f0f9ff; border:1px solid #bae6fd; border-radius:9px; font-size:.78rem; color:#0c4a6e; display:flex; align-items:flex-start; gap:7px; }

/* ── Edit pic row ───────────────────────────────────────── */
.edit-pic-row { display:flex; align-items:center; gap:.625rem; }
.edit-existing-pic { max-width:52px; max-height:52px; border-radius:9px; object-fit:cover; border:1px solid #e2e8f0; }
.edit-avatar-placeholder { width:52px; height:52px; border-radius:9px; background:linear-gradient(135deg,#dbeafe,#bfdbfe); color:#1e40af; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:.95rem; }

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width:576px) {
  .copy-devices-row { flex-direction:column; gap:10px; }
  .copy-arrow { transform:rotate(90deg); align-self:center; padding:4px 0; }
  .contacts-table thead th, .contacts-table tbody td { padding:.625rem .75rem; }
  .req-tab { font-size:.75rem; padding:9px 8px; }
}
</style>